import React, { ChangeEvent, useEffect, useState } from "react";
import { MailIcon, PhoneIcon, MapPinIcon, UploadIcon, InfoIcon } from "lucide-react";
import { Input } from "@component/input";
import { CVData } from "@type/cv";
import { uploadProfilePicture } from "@service/cv";
import ResumeAnalyzeTooltip from "./resume-analyze-tooltip";
import CountryCodeSelect from "@component/country-code-select";
import { COUNTRY_CODES, CountryCode } from "@component/country-codes-data";

interface PersonalInfoState {
  name: string;
  position: string;
  email: string;
  phone: string;
  country_code: string;
  location: string;
  avatar: string;
}

interface Props {
  isAnalized: boolean;
  isEditing: boolean;
  data: CVData | undefined;
  profile_picture_path: string
  onChange: (value: PersonalInfoState) => void;
}

export function PersonalInfo(props: Props) {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(COUNTRY_CODES.find((country) => country.code === props.data?.country_code) || {
    code: "+1",
    name: "United States",
    flag: "🇺🇸",
  });
  const [personalInfo, setPersonalInfo] = useState<PersonalInfoState>({
    name: props.data?.name || "",
    position: props.data?.position || "",
    email: props.data?.email || "",
    country_code: props.data?.country_code || "",
    phone: props.data?.phone || "",
    location: props.data?.location || "",
    avatar: props.profile_picture_path || "",
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPersonalInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    props.onChange(personalInfo);
  }, [personalInfo]);

  const handleFileChange = async (file: File) => {
    try {
      // Create a preview with FileReader
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          setPersonalInfo((prev) => ({
            ...prev,
            avatar: reader.result as string,
          }));
        }
      };
      reader.readAsDataURL(file);

      // Upload the file to server
      const response = await uploadProfilePicture(file);
      if (response.success && response.data) {
        // Update the avatar with the uploaded URL
        setPersonalInfo((prev) => ({
          ...prev,
          avatar: response.data?.profile_picture_path || prev.avatar,
        }));
      } else {
        throw new Error(response.message || "Failed to upload profile picture");
      }
    } catch (error) {
      console.error(
        "Failed to upload profile picture:",
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  };

  const handleCountryCodeChange = (countryCode: CountryCode) => {
    setPersonalInfo((prev) => ({
      ...prev,
      country_code: countryCode.code,
    }));

    setSelectedCountry(countryCode);
  }

  return (
    <>
      <div className="flex flex-col items-center mb-6">
        <div className="relative">
          <div className="w-36 h-36 rounded-full mb-2 relative">
            <div className="overflow-hidden w-36 h-36 rounded-full mb-2">
              <img
                onError={(e) => {
                  e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                }}
                src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${personalInfo?.avatar}`}
              />
            </div>
            {props.isAnalized && (
              <div className="absolute top-2 right-2">
                <ResumeAnalyzeTooltip
                  content={personalInfo?.avatar ? "Your profile picture is good." : "Your profile picture is missing. Add a professional photo to enhance your resume."}
                  type={personalInfo?.avatar ? "success" : "warning"}
                  onClose={() => { }}
                />
              </div>
            )}
          </div>
          {props.isEditing && (
            <div className="absolute bottom-4 right-3 cursor-pointer">
              <label
                htmlFor="avatar-upload"
                className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full cursor-pointer hover:bg-blue-700"
              >
                <UploadIcon className="h-4 w-4 text-white" />
              </label>
              <input
                type="file"
                accept="image/*"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileChange(e.target.files[0]);
                  }
                }}
              />
            </div>
          )}
        </div>
        {props.isEditing && (
          <div className="flex flex-row justify-center space-x-1">
            <InfoIcon className="w-4 h-4 text-blue-600" />
            <p className="text-xs mb-6"><b>Recommended</b>: Square image (e.g. 50×50 pixels)</p>
          </div>
        )}
        {props.isEditing ? (
          <div className="w-full space-y-2">
            <Input
              name="name"
              value={personalInfo?.name}
              onChange={handleInputChange}
              className="border-gray-300 text-black"
              placeholder="Name"
            />

            <Input
              name="position"
              value={personalInfo?.position}
              onChange={handleInputChange}
              className="border-gray-300 text-black"
              placeholder="Job Title"
            />
          </div>
        ) : (
          <>
            <h3 className={`text-xl font-bold ${personalInfo.position === "" ? "text-gray-400" : "text-black"}`}>
              {personalInfo?.name || "Add your name"}
            </h3>
            <p className={personalInfo.position === "" ? "text-gray-400" : "text-gray-600"}>{personalInfo?.position || "Add your job title"}</p>
          </>
        )}
      </div>

      <div className="space-y-3">
        {props.isEditing ? (
          <>
            <div className="flex items-center gap-3">
              <MailIcon className="h-5 w-5 text-gray-400" />
              <Input
                name="email"
                value={personalInfo?.email || ""}
                onChange={handleInputChange}
                className="border-gray-300 text-black"
                placeholder="Email"
              />
            </div>
            <div className="flex items-center gap-3">
              <PhoneIcon className="h-5 w-5 text-gray-400" />
              <div className="flex w-full">
                <CountryCodeSelect
                  value={selectedCountry}
                  onChange={handleCountryCodeChange}
                />
                <Input
                  name="phone"
                  value={personalInfo?.phone || ""}
                  onChange={handleInputChange}
                  className="border-gray-300 text-black rounded-l-none"
                  placeholder="Phone"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPinIcon className="h-5 w-5 text-gray-400" />
              <Input
                name="location"
                value={personalInfo?.location || ""}
                onChange={handleInputChange}
                className="border-gray-300 text-black"
                placeholder="Location"
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <MailIcon className="h-5 w-5 text-black" />
              <span className={personalInfo.email === "" ? "text-gray-400" : "text-black"}>{personalInfo?.email || "Add your email address"}</span>
            </div>
            <div className="flex items-center gap-3">
              <PhoneIcon className="h-5 w-5 text-black" />
                <span className={personalInfo.email === "" ? "text-gray-400" : "text-black"}>{personalInfo?.phone.length === 0 ? "" : personalInfo?.country_code}{personalInfo?.phone || "Add your phone number"}</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPinIcon className="h-5 w-5 text-black" />
              <span className={personalInfo.email === "" ? "text-gray-400" : "text-black"}>{personalInfo?.location || "Add your location"}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
